/**
 * Visualization Mode Types for Pose Overlay System
 */

import type { LandmarkDeviation } from './api';

export type VisualizationMode =
  | 'off'
  | 'ghost_echo'
  | 'deviation_vectors'
  | 'thermal_silhouette'
  | 'alignment_rings'
  | 'differential_skeleton'
  | 'particle_aura';

export interface VisualizationConfig {
  mode: VisualizationMode;
  intensity: number;           // 0-100
  showLabels: boolean;         // Region/landmark labels
  showScores: boolean;         // Score badges
  applyToRegions: boolean;     // Apply to body regions too
}

export type VisualizationTier = 'basic' | 'analysis' | 'effects';

export interface VisualizationModeInfo {
  id: VisualizationMode;
  label: string;
  shortLabel: string;
  description: string;
  tier: VisualizationTier;
  canCombine: boolean;
  defaultIntensity: number;
}

export const VISUALIZATION_MODES: Record<VisualizationMode, VisualizationModeInfo> = {
  off: {
    id: 'off',
    label: 'Off',
    shortLabel: 'Off',
    description: 'Clean video with no overlays',
    tier: 'basic',
    canCombine: false,
    defaultIntensity: 0,
  },
  ghost_echo: {
    id: 'ghost_echo',
    label: 'Ghost Echo',
    shortLabel: 'Ghost',
    description: 'Template pose as translucent overlay. Perfect for checking alignment.',
    tier: 'basic',
    canCombine: true,
    defaultIntensity: 40,
  },
  deviation_vectors: {
    id: 'deviation_vectors',
    label: 'Deviation Vectors',
    shortLabel: 'Vectors',
    description: 'Arrows showing where to move each body part. Great for corrections.',
    tier: 'analysis',
    canCombine: true,
    defaultIntensity: 80,
  },
  thermal_silhouette: {
    id: 'thermal_silhouette',
    label: 'Thermal Silhouette',
    shortLabel: 'Thermal',
    description: 'Heat map coloring by alignment quality. Spot problems instantly.',
    tier: 'analysis',
    canCombine: false,
    defaultIntensity: 70,
  },
  alignment_rings: {
    id: 'alignment_rings',
    label: 'Alignment Rings',
    shortLabel: 'Rings',
    description: 'Target zones around landmarks. Hit the bullseye for perfect form.',
    tier: 'basic',
    canCombine: true,
    defaultIntensity: 60,
  },
  differential_skeleton: {
    id: 'differential_skeleton',
    label: 'Differential Skeleton',
    shortLabel: 'Skeleton',
    description: "Skeleton colored by each bone's alignment. Identify weak joints.",
    tier: 'analysis',
    canCombine: false,
    defaultIntensity: 90,
  },
  particle_aura: {
    id: 'particle_aura',
    label: 'Particle Aura',
    shortLabel: 'Aura',
    description: 'Animated particles showing form quality. Engaging and dynamic.',
    tier: 'effects',
    canCombine: true,
    defaultIntensity: 50,
  },
};

export const DEFAULT_VISUALIZATION_CONFIG: VisualizationConfig = {
  mode: 'ghost_echo',
  intensity: 40,
  showLabels: true,
  showScores: true,
  applyToRegions: true,
};

// Extended landmark deviation with coordinates (matches backend)
// This is now an alias to LandmarkDeviation from api.ts which includes all coordinate fields
export type LandmarkDeviationWithCoords = LandmarkDeviation;
